// src/admin/AdminTermometreiro.js
import React, { useEffect, useMemo, useState } from "react";
import { db, auth } from "../firebase";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  query,
  orderBy,
  limit,
} from "firebase/firestore";

import { calcularPopularidadePonderada } from "../services/calcularPopularidadePonderada";
import { getTotalPosicoes, categoriasPorModo } from "../services/estruturaCategorias";
import nomesBonitosCategoria from "../utils/nomesBonitosCategoria";

// Ordem fixa dos modos para o Top 25 (além do "total")
const RANKING_ORDER = [
  "total",
  "oscar-vencedores", "oscar-indicados",
  "globodeouro-vencedores", "globodeouro-indicados",
  "bafta-vencedores", "bafta-indicados",
  "sagawards-vencedores", "sagawards-indicados",
  "criticschoice-vencedores", "criticschoice-indicados",
  "dga-vencedores", "dga-indicados",
  "pga-vencedores", "pga-indicados",
];

// onde guardamos season e especialistas
const REF_CONFIG = doc(db, "termometreiro", "config");
const REF_ESPECIALISTAS = doc(db, "termometreiro", "especialistas");

const URL_PUBLICAR_GITHUB =
  "https://publicarsnapshotgithub-2ygc4ukazq-uc.a.run.app";

function isoToDateSafe(v) {
  if (!v || typeof v !== "string") return null;
  const d = new Date(v);
  return isNaN(d) ? null : d;
}

function normalizarListaUIDs(text) {
  const linhas = (text || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

  const vistos = new Set();
  const saida = [];

  for (const uid of linhas) {
    if (!vistos.has(uid)) {
      vistos.add(uid);
      saida.push(uid);
    }
  }

  return saida;
}

// Processa uma lista em lotes para evitar rajadas gigantes de leituras no Firestore
async function processarEmLotes(lista, tamanhoLote, fn) {
  for (let i = 0; i < lista.length; i += tamanhoLote) {
    const lote = lista.slice(i, i + tamanhoLote);
    await Promise.all(lote.map(fn));
  }
}

export default function AdminTermometreiro() {
  // UI state
  const [season, setSeason] = useState("2026");
  const [uidsText, setUidsText] = useState("");
  const [savingList, setSavingList] = useState(false);
  const [savingSeason, setSavingSeason] = useState(false);
  const [msg, setMsg] = useState("");
  const [building, setBuilding] = useState(false);
  const [downloadURL, setDownloadURL] = useState("");

  // carregar config/season + especialistas/uids
  useEffect(() => {
    let ativo = true;

    (async () => {
      try {
        const [cfg, esp] = await Promise.all([
          getDoc(REF_CONFIG),
          getDoc(REF_ESPECIALISTAS),
        ]);

        if (!ativo) return;

        if (cfg.exists()) {
          const s = cfg.data()?.season;
          if (s) setSeason(String(s));
        }

        if (esp.exists()) {
          const u = esp.data()?.uids || [];
          setUidsText(u.join("\n"));
        }
      } catch (e) {
        console.error("Erro ao carregar config/especialistas:", e);
        setMsg("Erro ao carregar configuração.");
      }
    })();

    return () => {
      ativo = false;
    };
  }, []);

  async function salvarSeason() {
    setSavingSeason(true);
    setMsg("");

    try {
      await setDoc(
        REF_CONFIG,
        {
          season: String(season),
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      setMsg("Temporada guardada.");
    } catch (e) {
      console.error(e);
      setMsg("Erro ao guardar temporada.");
    } finally {
      setSavingSeason(false);
    }
  }

  async function salvarUids() {
    setSavingList(true);
    setMsg("");

    try {
      const uids = normalizarListaUIDs(uidsText);

      await setDoc(
        REF_ESPECIALISTAS,
        {
          uids,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      setMsg(`Lista de especialistas guardada (${uids.length}).`);
    } catch (e) {
      console.error(e);
      setMsg("Erro ao guardar lista.");
    } finally {
      setSavingList(false);
    }
  }

  // ==== BUILDERS ============================================================

  // carrega perfis de usuários para enriquecer (nome/sobrenome/estado/fotoPerfil)
  async function fetchPerfis(uids) {
    const out = new Map();

    await Promise.all(
      Array.from(uids).map(async (uid) => {
        try {
          const s = await getDoc(doc(db, "usuarios", uid));

          if (s.exists()) {
            const d = s.data() || {};
            out.set(uid, {
              uid,
              nome: d.nome || "",
              sobrenome: d.sobrenome || "",
              estado: d.estado || "",
              fotoPerfil: d.fotoPerfil || "",
              criadoEm: d.criadoEm || null,
              especialista: !!d.especialista,
            });
          } else {
            out.set(uid, {
              uid,
              nome: "",
              sobrenome: "",
              estado: "",
              fotoPerfil: "",
              criadoEm: null,
              especialista: false,
            });
          }
        } catch {
          out.set(uid, {
            uid,
            nome: "",
            sobrenome: "",
            estado: "",
            fotoPerfil: "",
            criadoEm: null,
            especialista: false,
          });
        }
      })
    );

    return out;
  }

  // RANKING TOP 25 — versão em lotes para evitar excesso de leituras simultâneas
  async function buildRankingTop25() {
    console.log("[TOP25] A carregar usuários...");
    const usersSnap = await getDocs(collection(db, "usuarios"));
    const uids = usersSnap.docs.map((d) => d.id);
    console.log("[TOP25] Total de usuários:", uids.length);

    const byUid = new Map();

    await processarEmLotes(uids, 25, async (uid) => {
      try {
        const pRef = doc(db, "ranking", "usuarios", uid, "pontos");
        const pSnap = await getDoc(pRef);
        if (!pSnap.exists()) return;

        const data = pSnap.data() || {};
        const agg = { total: 0, modes: {} };

        if (typeof data.total === "number") {
          agg.total = data.total;
        }

        // cada modo (ex: "oscar-indicados", "globodeouro-vencedores", etc.)
        Object.keys(data).forEach((k) => {
          if (k === "total") return;

          const bloco = data[k];
          if (!bloco || typeof bloco !== "object") return;

          const pontosmodo =
            typeof bloco.pontosmodo === "number" ? bloco.pontosmodo : 0;
          const ultima = bloco.ultimaAtualizacao || null;

          agg.modes[k] = {
            pontos: pontosmodo,
            ultimaAtualizacao: ultima,
          };
        });

        byUid.set(uid, agg);
      } catch (e) {
        console.warn("Falha lendo pontos de", uid, e);
      }
    });

    function sorter(modoKey) {
      return (a, b) => {
        const pa = byUid.get(a)?.modes?.[modoKey]?.pontos ?? 0;
        const pb = byUid.get(b)?.modes?.[modoKey]?.pontos ?? 0;
        if (pb !== pa) return pb - pa;

        // desempate: última atualização MAIS ANTIGA vence
        const ua =
          isoToDateSafe(byUid.get(a)?.modes?.[modoKey]?.ultimaAtualizacao)?.getTime() ??
          Number.POSITIVE_INFINITY;
        const ub =
          isoToDateSafe(byUid.get(b)?.modes?.[modoKey]?.ultimaAtualizacao)?.getTime() ??
          Number.POSITIVE_INFINITY;

        return ua - ub;
      };
    }

    function sorterTotal(a, b) {
      const ta = byUid.get(a)?.total ?? 0;
      const tb = byUid.get(b)?.total ?? 0;
      if (tb !== ta) return tb - ta;

      // pega a MAIS ANTIGA entre as últimas atualizações que o usuário tiver
      const datesA = Object.values(byUid.get(a)?.modes || {})
        .map((m) => isoToDateSafe(m.ultimaAtualizacao)?.getTime())
        .filter(Boolean);

      const datesB = Object.values(byUid.get(b)?.modes || {})
        .map((m) => isoToDateSafe(m.ultimaAtualizacao)?.getTime())
        .filter(Boolean);

      const minA = datesA.length ? Math.min(...datesA) : Number.POSITIVE_INFINITY;
      const minB = datesB.length ? Math.min(...datesB) : Number.POSITIVE_INFINITY;

      return minA - minB;
    }

    const allUids = Array.from(byUid.keys());
    const modesOut = {};

    // total
    const topTotalUids = [...allUids]
      .sort(sorterTotal)
      .slice(0, 25)
      .filter((uid) => (byUid.get(uid)?.total ?? 0) > 0);

    let perfis = await fetchPerfis(new Set(topTotalUids));

    modesOut.total = topTotalUids.map((uid) => ({
      uid,
      pontos: byUid.get(uid)?.total ?? 0,
      ultimaAtualizacao: null,
      nome: perfis.get(uid)?.nome || "",
      sobrenome: perfis.get(uid)?.sobrenome || "",
      estado: perfis.get(uid)?.estado || "",
      fotoPerfil: perfis.get(uid)?.fotoPerfil || "",
    }));

    // por modo, usando a ordem fixa
    for (const modoKey of RANKING_ORDER.filter((k) => k !== "total")) {
      const topUids = [...allUids]
        .filter((uid) => (byUid.get(uid)?.modes?.[modoKey]?.pontos ?? 0) > 0)
        .sort(sorter(modoKey))
        .slice(0, 25);

      if (!topUids.length) continue;

      perfis = await fetchPerfis(new Set(topUids));

      modesOut[modoKey] = topUids.map((uid) => {
        const m = byUid.get(uid)?.modes?.[modoKey] || {};
        return {
          uid,
          pontos: m.pontos ?? 0,
          ultimaAtualizacao: m.ultimaAtualizacao || null,
          nome: perfis.get(uid)?.nome || "",
          sobrenome: perfis.get(uid)?.sobrenome || "",
          estado: perfis.get(uid)?.estado || "",
          fotoPerfil: perfis.get(uid)?.fotoPerfil || "",
        };
      });
    }

    const order = RANKING_ORDER.filter((k) => modesOut[k]?.length);

    console.log(
      "[TOP25] Totais por chave:",
      order.reduce((acc, k) => ({ ...acc, [k]: modesOut[k].length }), {})
    );

    return { order, modes: modesOut };
  }

  // NOVOS USUÁRIOS (10)
  async function buildNovosUsuarios() {
    console.log("[NOVOS] A carregar novos usuários...");
    const q = query(collection(db, "usuarios"), orderBy("criadoEm", "desc"), limit(10));
    const snap = await getDocs(q);

    const out = [];
    snap.forEach((d) => {
      const v = d.data() || {};
      out.push({
        uid: d.id,
        nome: v.nome || "",
        sobrenome: v.sobrenome || "",
        estado: v.estado || "",
        fotoPerfil: v.fotoPerfil || "",
        criadoEm: v.criadoEm || null,
      });
    });

    console.log("[NOVOS] Total carregado:", out.length);
    return out;
  }

  // ESPECIALISTAS RECENTES (dos UIDs informados)
  async function buildEspecialistasRecentes(currSeason, uidsLista) {
    console.log("[ESPECIALISTAS RECENTES] Total de UIDs:", uidsLista.length);

    if (!uidsLista.length) return [];

    const perfis = await fetchPerfis(new Set(uidsLista));
    const out = [];

    await Promise.all(
      uidsLista.map(async (uid) => {
        let last = null;

        try {
          const s = await getDoc(
            doc(db, "usuarios", uid, `snapDropdown${currSeason}`, "dados")
          );

          if (s.exists()) {
            const d = s.data() || {};

            // d = { "oscar-indicados": { melhoranimacao: {..., dataAtualizacao}, ... }, ... }
            for (const modo of Object.keys(d)) {
              const cats = d[modo] || {};

              for (const cat of Object.keys(cats)) {
                const blocoCat = cats[cat] || {};
                const iso = blocoCat?.dataAtualizacao || null;

                if (iso) {
                  const dt = isoToDateSafe(iso);
                  if (dt && (!last || dt > last)) last = dt;
                }
              }

              const isoModo = d[modo]?.dataAtualizacao || null;
              if (isoModo) {
                const dtModo = isoToDateSafe(isoModo);
                if (dtModo && (!last || dtModo > last)) last = dtModo;
              }
            }
          }
        } catch (e) {
          console.warn("Falha lendo snapDropdown para", uid, e);
        }

        out.push({
          uid,
          nome: perfis.get(uid)?.nome || "",
          sobrenome: perfis.get(uid)?.sobrenome || "",
          estado: perfis.get(uid)?.estado || "",
          fotoPerfil: perfis.get(uid)?.fotoPerfil || "",
          ultimaAtualizacao: last ? last.toISOString() : null,
        });
      })
    );

    out.sort((a, b) => {
      const ta = isoToDateSafe(a.ultimaAtualizacao)?.getTime() ?? 0;
      const tb = isoToDateSafe(b.ultimaAtualizacao)?.getTime() ?? 0;
      return tb - ta;
    });

    return out.slice(0, 10);
  }

  // MAIS APOSTADOS (usuarios e especialistas)
  async function buildMaisApostados() {
    console.log("[MAIS APOSTADOS] Início...");
    const modos = RANKING_ORDER.filter((m) => m !== "total");

    async function getCategoriasDoModo(modo) {
      try {
        const snap = await getDocs(collection(db, "modos", modo, "categorias"));
        return snap.docs.map((d) => d.id);
      } catch {
        return categoriasPorModo[modo] || [];
      }
    }

    async function montarPara(tipo) {
      const raiz = tipo === "usuarios" ? "popularidade" : "popularidadeespecialistas";
      const payload = {};

      for (const modo of modos) {
        const cats = await getCategoriasDoModo(modo);

        for (const categoria of cats) {
          const docRef = doc(db, raiz, modo, "categorias", categoria);
          const s = await getDoc(docRef);
          if (!s.exists()) continue;

          const d = s.data() || {};
          const candidatos = d.candidatos || {};
          const totalUsuarios = d.totalUsuarios ?? 0;
          if (!totalUsuarios) continue;

          const totalPosicoes = getTotalPosicoes(modo, categoria) || 10;
          const pesos = calcularPopularidadePonderada(
            candidatos,
            totalPosicoes,
            totalUsuarios
          );

          const itens = Object.entries(candidatos)
            .map(([k, val]) => ({
              idInterno: val.idInterno || k,
              titulo: val.titulo || "",
              filme: val.filme || "",
              pais: val.pais || "",
              imagem: val.imagem || "",
              porcentagem: pesos[k] ?? 0,
            }))
            .sort((a, b) => (b.porcentagem || 0) - (a.porcentagem || 0));

          if (!itens.length) continue;

          if (!payload[modo]) payload[modo] = {};
          payload[modo][categoria] = {
            totalUsuarios,
            totalPosicoes,
            itens,
          };
        }
      }

      return payload;
    }

    const usuarios = await montarPara("usuarios");
    const especialistas = await montarPara("especialistas");

    console.log("[MAIS APOSTADOS] Finalizado.");
    return { usuarios, especialistas };
  }

  async function buildSnapshotCompleto() {
    setBuilding(true);
    setMsg("");
    setDownloadURL("");

    try {
      console.log("=== INÍCIO SNAPSHOT ===");
      console.log("navigator.onLine:", navigator.onLine);
      console.log("db existe?", !!db);

      // lê season e especialistas atuais salvos
      console.log("[SNAPSHOT] A ler config e especialistas...");
      const [cfgSnap, espSnap] = await Promise.all([
        getDoc(REF_CONFIG),
        getDoc(REF_ESPECIALISTAS),
      ]);

      const currSeason =
        cfgSnap.exists() && cfgSnap.data()?.season
          ? String(cfgSnap.data().season)
          : String(season);

      const uidsEsp =
        espSnap.exists() && Array.isArray(espSnap.data()?.uids)
          ? espSnap.data().uids
          : normalizarListaUIDs(uidsText);

      console.log("[SNAPSHOT] Season:", currSeason);
      console.log("[SNAPSHOT] Total especialistas:", uidsEsp.length);

      // builders em sequência para descobrir exatamente onde quebra
      console.log("[SNAPSHOT] 1. buildRankingTop25");
      const ranking = await buildRankingTop25();

      console.log("[SNAPSHOT] 2. buildNovosUsuarios");
      const novos = await buildNovosUsuarios();

      console.log("[SNAPSHOT] 3. buildEspecialistasRecentes");
      const espRec = await buildEspecialistasRecentes(currSeason, uidsEsp);

      console.log("[SNAPSHOT] 4. buildMaisApostados");
      const mais = await buildMaisApostados();

      const snapshot = {
        version: 1,
        generatedAt: new Date().toISOString(),
        season: currSeason,
        ranking,
        novosUsuarios: novos,
        especialistasRecentes: espRec,
        maisApostados: mais,
      };

      const json = JSON.stringify(snapshot, null, 2);

      const snapshotVersion = {
        version: snapshot.version,
        generatedAt: snapshot.generatedAt,
        season: snapshot.season,
      };

      const versionJson = JSON.stringify(snapshotVersion);
      const jsPayload = `window.__TMTR_SNAPSHOT__ = ${json};`;

      const usuarioAtual = auth.currentUser;

      if (!usuarioAtual) {
        throw new Error("Nenhum usuário logado. Faça login novamente.");
      }

      const idToken = await usuarioAtual.getIdToken(true);

      const resposta = await fetch(URL_PUBLICAR_GITHUB, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          snapshotJson: json,
          versionJson,
          snapshotJs: jsPayload,
        }),
      });

      if (!resposta.ok) {
        const erroTexto = await resposta.text();
        throw new Error(`Erro ao publicar no GitHub: ${erroTexto}`);
      }

      const githubUrl =
        "https://raw.githubusercontent.com/renanmorg/termometreiro-snapshots/main/dist/termometreiro/snapshot.json";

      setDownloadURL(githubUrl);
      setMsg("Snapshot publicado no GitHub com sucesso.");
      console.log("=== SNAPSHOT FINALIZADO COM SUCESSO ===");
    } catch (e) {
      console.error("Erro ao gerar snapshot:", e);

      if (
        e?.code === "unavailable" ||
        String(e?.message || "").toLowerCase().includes("offline")
      ) {
        setMsg("Erro ao gerar snapshot. O Firestore ficou indisponível ou offline durante a leitura.");
      } else {
        setMsg("Erro ao gerar snapshot. Veja o console.");
      }
    } finally {
      setBuilding(false);
    }
  }

  // ==== UI =================================================================

  const totalUids = useMemo(() => normalizarListaUIDs(uidsText).length, [uidsText]);

  return (
    <div style={{ marginTop: 24 }}>
      <h2>Termometreiro — Snapshot estático para o Home</h2>

      {/* Bloco Season */}
      <div
        style={{
          margin: "16px 0",
          padding: 12,
          border: "1px solid #ddd",
          borderRadius: 8,
        }}
      >
        <h3 style={{ marginTop: 0 }}>Temporada</h3>
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <label>
            <strong>Season:</strong>
          </label>
          <input
            type="text"
            value={season}
            onChange={(e) => setSeason(e.target.value)}
            style={{ width: 120 }}
          />
          <button className="btn" onClick={salvarSeason} disabled={savingSeason}>
            {savingSeason ? "Guardando..." : "Guardar temporada"}
          </button>
        </div>
      </div>

      {/* Bloco UIDs Especialistas */}
      <div
        style={{
          margin: "16px 0",
          padding: 12,
          border: "1px solid #ddd",
          borderRadius: 8,
        }}
      >
        <h3 style={{ marginTop: 0 }}>Especialistas monitorados (UIDs)</h3>
        <p style={{ marginTop: 0 }}>
          Cole <em>um UID por linha</em>. Apenas estes UIDs entram no “Especialistas recentes”.
        </p>
        <textarea
          rows={10}
          style={{ width: "100%", boxSizing: "border-box" }}
          value={uidsText}
          onChange={(e) => setUidsText(e.target.value)}
          placeholder={"uid_1\nuid_2\nuid_3"}
        />
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 8 }}>
          <button className="btn" onClick={salvarUids} disabled={savingList}>
            {savingList ? "Guardando..." : "Guardar lista"}
          </button>
          <span>
            Total de UIDs: <strong>{totalUids}</strong>
          </span>
        </div>
      </div>

      {/* Ação principal */}
      <div
        style={{
          margin: "16px 0",
          padding: 12,
          border: "1px solid #ddd",
          borderRadius: 8,
        }}
      >
        <h3 style={{ marginTop: 0 }}>Gerar snapshot completo</h3>
        <p style={{ marginTop: 0 }}>
          Monta Top 25 (Total e por modo), Novos usuários (10), Especialistas recentes
          (lista acima) e Mais apostados (Usuários/Especialistas).
        </p>
        <button className="btn" onClick={buildSnapshotCompleto} disabled={building}>
          {building ? "A gerar..." : "Gerar snapshot completo"}
        </button>

        {downloadURL && (
          <p style={{ marginTop: 10 }}>
            <a href={downloadURL} target="_blank" rel="noreferrer">
              Abrir snapshot.json
            </a>
          </p>
        )}
      </div>

      {msg && <p style={{ marginTop: 8 }}>{msg}</p>}
    </div>
  );
}